import { Building2, UserRound, MapPin } from "lucide-react";
import EmployerMediaSection from "./EmployerMediaSection.jsx";

export const CREATE_EMPLOYER_POLICY_NOTE = "SuperAdmin เลือก Agent ได้ ส่วน Agent จะสร้างได้เฉพาะสายงานของตนเอง";
export const TAX_ID_FORM_NOTE = "เลขผู้เสียภาษีจะถูกเก็บแบบปิดบังในทุกหน้าจอและ audit log ไม่แสดงเลขเต็มที่ใดในระบบ";
export const TAX_ID_DETAIL_NOTE = "เลขผู้เสียภาษีแสดงแบบปิดบังเสมอ ไม่แสดงเลขเต็มในหน้านี้หรือใน audit log";

/**
 * P160: สำนักงานใหญ่ is branch code 00000 on every Thai tax invoice, and a
 * branch is its five-digit number. Offered as a picker plus a free field
 * rather than free text alone, because "สนญ."/"สำนักงานใหญ่"/"HQ" typed three
 * ways is three different values to anything that reads them.
 */
export const TAX_BRANCH_HEAD_OFFICE = "00000";

/** The three statuses the employer record itself can hold. */
const EMPLOYER_STATUS_OPTIONS = [
  { value: "Active", label: "ใช้งานอยู่" },
  { value: "Suspended", label: "ระงับชั่วคราว" },
  { value: "Archived", label: "เก็บเข้าคลัง (ไม่ใช้งานแล้ว)" },
];

const EDIT_UNCHANGED_NOTE =
  "ช่องที่เว้นว่างไว้จะคงค่าเดิมที่บันทึกไว้ — หน้านี้ยังไม่รองรับการล้างค่ากลับให้ว่าง";
const EDIT_CONTACT_CHANNEL_NOTE = "ช่องทางติดต่อด้านบนเป็นค่าที่บันทึกไว้ ยังแก้ไขจากหน้านี้ไม่ได้";
const EDIT_TAX_ID_NOTE = "เว้นว่างไว้เพื่อคงเลขเดิม กรอกเลข 13 หลักใหม่เมื่อต้องการแทนที่เท่านั้น";
const EDIT_OWNER_NOTE = "Agent เจ้าของและสายงานเปลี่ยนจากหน้านี้ไม่ได้";

/**
 * P160: one section of the form - an icon, a title, a one-line reason the
 * section exists, then its fields. The shape follows the placement employer
 * form in the Front reference repo, which groups by what the operator is
 * thinking about rather than presenting one flat wall of inputs.
 */
function FormSection({ icon: Icon, title, sub, children }) {
  return (
    <section className="wx-tj-formsec">
      <header className="wx-tj-formsec-head">
        <span className="wx-tj-formsec-icon" aria-hidden="true">
          <Icon size={16} />
        </span>
        <div>
          <h3>{title}</h3>
          {sub && <p>{sub}</p>}
        </div>
      </header>
      <div className="wx-tj-formsec-body">{children}</div>
    </section>
  );
}

/**
 * A select over a closed vocabulary served by the API. While the list is
 * loading, or if it failed to load, the control says so instead of rendering
 * an empty dropdown that reads as "there are no choices".
 */
function VocabularySelect({ label, value, onChange, options, placeholder, required }) {
  const loading = options == null;
  const empty = !loading && options.length === 0;
  return (
    <div className="wx-tj-field">
      <label htmlFor={`wx-tj-${label}`}>
        {label} {required && <b>*</b>}
      </label>
      <select
        id={`wx-tj-${label}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={loading || empty}
        required={required}
      >
        <option value="">{loading ? "กำลังโหลดตัวเลือก..." : empty ? "โหลดตัวเลือกไม่สำเร็จ" : placeholder}</option>
        {(options ?? []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * The employer record as a form - the same screen whether it is being created
 * or edited.
 *
 * mode="create" posts a new employer; mode="edit" is that identical screen
 * opened on an existing record, every field already carrying what is stored,
 * with Save. Two differences are not cosmetic and both come from the real
 * update contract rather than from taste:
 *
 *  - Agent เจ้าของ is read-only when editing. Ownership is not transferable
 *    through the update endpoint, so offering a picker would promise a change
 *    that silently never happens.
 *  - เลขผู้เสียภาษี is never prefilled. The detail response only ever returns
 *    the masked value (it is masked everywhere in this system, audit log
 *    included), so prefilling the field would either show a number nobody may
 *    see or save the dots over the real one. The stored value is shown masked,
 *    and the input replaces it only when an operator types a new one.
 *
 * Phone/Line are absent when editing for the same honesty reason: the update
 * contract has no field for either, so an input for them would be a lie.
 */
export default function EmployerFormView({
  titleId,
  mode = "create",
  // Rendered inside the record drawer, which already carries the employer's
  // name, code and status above its tabs - a second title under it would say
  // the same thing twice.
  embedded = false,
  form,
  onChange,
  onSubmit,
  onCancel,
  submitState,
  errorText,
  agentOptions = [],
  ownAgentId,
  primaryRole,
  employerOptions,
  employer,
  // P161: called with the re-read employer after any media write, so the drawer
  // that owns this form can refresh without the form guessing what changed.
  onMediaChanged,
}) {
  const isEdit = mode === "edit";
  const submitting = submitState.status === "submitting";
  const isAgentRole = primaryRole === "Agent";
  const lockedAgent = isAgentRole ? agentOptions.find((agent) => agent.agentId === ownAgentId) : null;
  const isHeadOffice = form.taxBranchCode === TAX_BRANCH_HEAD_OFFICE;
  // A company cannot have been established tomorrow. Bounding the picker is
  // cheaper than validating the typo after it is stored.
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="wx-tj-drawer">
      {embedded ? null : (
        <>
          <span className="wx-tj-drawer-kicker">{isEdit ? "EDIT EMPLOYER" : "CREATE EMPLOYER"}</span>
          <h2 id={titleId} className="wx-tj-drawer-title">
            {isEdit ? "แก้ไขข้อมูลนายจ้าง" : "สร้างนายจ้างใหม่"}
          </h2>
          <p className="wx-tj-drawer-sub">
            {isEdit ? (
              <>
                ข้อมูลที่บันทึกไว้ของนายจ้างรายนี้ แก้ไขได้ทุกช่องแล้วกด <b>บันทึกการแก้ไข</b>
              </>
            ) : (
              <>
                กรอกข้อมูลบริษัทให้ครบถ้วน ยิ่งละเอียดยิ่งจับคู่แรงงานกับงานได้แม่นขึ้น — มีเฉพาะช่องที่ติด <b>*</b> เท่านั้นที่บังคับกรอก
              </>
            )}
          </p>
        </>
      )}

      <form onSubmit={onSubmit}>
        {/* The create form still needs its ownership policy up front. The edit
            form's "empty means unchanged" caveat moved down beside Save: it
            matters when saving, not when arriving. */}
        {!isEdit && <div className="wx-tj-notice">{CREATE_EMPLOYER_POLICY_NOTE}</div>}

        {isEdit && !embedded && (
          <div className="wx-tj-context-pill">
            <strong>{employer?.legalName ?? "-"}</strong> · {employer?.employerCode ?? "-"}
          </div>
        )}

        <FormSection icon={Building2} title="ข้อมูลบริษัท" sub="ชื่อและสถานะทางกฎหมายของนายจ้าง">
          <div className="wx-tj-field">
            <label htmlFor="wx-tj-agent">
              Agent เจ้าของ {!isEdit && <b>*</b>}
            </label>
            {isEdit ? (
              <input id="wx-tj-agent" type="text" value={`${employer?.agent?.agentCode ?? "-"} · ${employer?.agent?.displayName ?? "-"}`} disabled readOnly />
            ) : isAgentRole ? (
              <input
                id="wx-tj-agent"
                type="text"
                value={lockedAgent ? `${lockedAgent.agentCode} · ${lockedAgent.displayName}` : ownAgentId ?? ""}
                disabled
                readOnly
              />
            ) : (
              <select id="wx-tj-agent" value={form.agentId} onChange={(event) => onChange("agentId", event.target.value)} required>
                <option value="">เลือก Agent</option>
                {agentOptions.map((agent) => (
                  <option key={agent.agentId} value={agent.agentId}>
                    {agent.agentCode} · {agent.displayName}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="wx-tj-two">
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-legalname">
                ชื่อนิติบุคคล (Legal Name) <b>*</b>
              </label>
              <input
                id="wx-tj-legalname"
                type="text"
                placeholder="บริษัท ... จำกัด"
                value={form.legalName}
                onChange={(event) => onChange("legalName", event.target.value)}
                required
              />
            </div>
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-displayname">ชื่อที่ใช้แสดง (Display Name)</label>
              <input
                id="wx-tj-displayname"
                type="text"
                placeholder="ชื่อย่อสำหรับแสดงในระบบ"
                value={form.displayName}
                onChange={(event) => onChange("displayName", event.target.value)}
              />
            </div>
          </div>

          <div className="wx-tj-two">
            <VocabularySelect
              label="ประเภทนิติบุคคล"
              value={form.entityType}
              onChange={(value) => onChange("entityType", value)}
              options={employerOptions?.entityTypes}
              placeholder="เลือกประเภทนิติบุคคล"
            />
            <VocabularySelect
              label="ประเภทธุรกิจ"
              value={form.businessType}
              onChange={(value) => onChange("businessType", value)}
              options={employerOptions?.businessTypes}
              placeholder="เลือกประเภทธุรกิจ"
            />
          </div>

          <div className="wx-tj-two">
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-taxid">{isEdit ? "เปลี่ยนเลขผู้เสียภาษี (Tax ID)" : "เลขผู้เสียภาษี (Tax ID)"}</label>
              {isEdit && (
                <div className="wx-tj-stored-value">
                  เลขที่บันทึกไว้ <strong className="wx-tj-code">{employer?.taxIdMasked ?? "-"}</strong>
                </div>
              )}
              <input
                id="wx-tj-taxid"
                type="text"
                inputMode="numeric"
                maxLength={13}
                placeholder={isEdit ? "เว้นว่างไว้เพื่อคงเลขเดิม" : "เลข 13 หลัก"}
                value={form.taxId}
                onChange={(event) => onChange("taxId", event.target.value)}
              />
              {isEdit && <p className="wx-tj-field-note">{EDIT_TAX_ID_NOTE}</p>}
            </div>
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-branch">สาขาผู้เสียภาษี</label>
              <div className="wx-tj-inline-choice">
                <label className="wx-tj-checkline">
                  <input
                    type="checkbox"
                    checked={isHeadOffice}
                    onChange={(event) => onChange("taxBranchCode", event.target.checked ? TAX_BRANCH_HEAD_OFFICE : "")}
                  />
                  สำนักงานใหญ่
                </label>
                <input
                  id="wx-tj-branch"
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="เลขสาขา 5 หลัก"
                  value={isHeadOffice ? "" : form.taxBranchCode}
                  onChange={(event) => onChange("taxBranchCode", event.target.value)}
                  disabled={isHeadOffice}
                />
              </div>
            </div>
          </div>

          <div className="wx-tj-two">
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-established">วันที่จดทะเบียน</label>
              <input
                id="wx-tj-established"
                type="date"
                max={today}
                value={form.establishedOn}
                onChange={(event) => onChange("establishedOn", event.target.value)}
              />
            </div>
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-capital">ทุนจดทะเบียน (บาท)</label>
              <input
                id="wx-tj-capital"
                type="number"
                min="0"
                step="1000"
                placeholder="เช่น 5000000"
                value={form.registeredCapital}
                onChange={(event) => onChange("registeredCapital", event.target.value)}
              />
            </div>
          </div>

          <div className={isEdit ? "wx-tj-two" : undefined}>
            <VocabularySelect
              label="ขนาดกิจการ"
              value={form.employeeCountRange}
              onChange={(value) => onChange("employeeCountRange", value)}
              options={employerOptions?.employeeCountRanges}
              placeholder="เลือกจำนวนพนักงาน"
            />
            {isEdit && (
              <div className="wx-tj-field">
                <label htmlFor="wx-tj-employerstatus">สถานะนายจ้าง</label>
                <select id="wx-tj-employerstatus" value={form.employerStatus} onChange={(event) => onChange("employerStatus", event.target.value)}>
                  {EMPLOYER_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="wx-tj-field">
            <label htmlFor="wx-tj-background">ความเป็นมาบริษัท</label>
            <textarea
              id="wx-tj-background"
              rows={4}
              maxLength={2000}
              placeholder="ธุรกิจหลัก ประสบการณ์ และลักษณะงานที่รับ — ช่วยให้ทีมจับคู่งานเข้าใจว่านายจ้างรายนี้ทำอะไร"
              value={form.companyBackground}
              onChange={(event) => onChange("companyBackground", event.target.value)}
            />
          </div>
        </FormSection>

        <FormSection icon={UserRound} title="ผู้ติดต่อ" sub="คนที่ทีมงานจะติดต่อกลับเมื่อมีเรื่องเรื่องงานหรือแรงงาน">
          <div className="wx-tj-two">
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-contactname">
                ชื่อผู้ติดต่อหลัก <b>*</b>
              </label>
              <input
                id="wx-tj-contactname"
                type="text"
                placeholder="ชื่อ-นามสกุล"
                value={form.primaryContactName}
                onChange={(event) => onChange("primaryContactName", event.target.value)}
                required
              />
            </div>
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-contactposition">ตำแหน่ง</label>
              <input
                id="wx-tj-contactposition"
                type="text"
                placeholder="เช่น ผู้จัดการฝ่ายบุคคล"
                value={form.primaryContactPosition}
                onChange={(event) => onChange("primaryContactPosition", event.target.value)}
              />
            </div>
          </div>

          {isEdit ? (
            <>
              {/* The stored channels, shown where the contact is edited rather
                  than behind a tab of their own. Read-only because the update
                  contract has no field for either of them - an input here would
                  promise a change that silently never happens. */}
              <div className="wx-tj-channels">
                {(employer?.contacts ?? []).length === 0 ? (
                  <p className="wx-tj-field-note">ยังไม่มีช่องทางติดต่อที่บันทึกไว้</p>
                ) : (
                  (employer?.contacts ?? []).map((contact, index) => (
                    <div className="wx-tj-kv-row" key={`${contact.contactType}-${index}`}>
                      <span>{contact.contactType ?? "ช่องทางติดต่อ"}</span>
                      <strong>{contact.contactValue ?? "-"}</strong>
                    </div>
                  ))
                )}
              </div>
              <p className="wx-tj-field-note">{EDIT_CONTACT_CHANNEL_NOTE}</p>
            </>
          ) : (
            <div className="wx-tj-two">
              <div className="wx-tj-field">
                <label htmlFor="wx-tj-phone">
                  เบอร์โทรผู้ติดต่อหลัก <b>*</b>
                </label>
                <input
                  id="wx-tj-phone"
                  type="tel"
                  placeholder="0XX-XXX-XXXX"
                  value={form.primaryPhoneNumber}
                  onChange={(event) => onChange("primaryPhoneNumber", event.target.value)}
                  required
                />
              </div>
              <div className="wx-tj-field">
                <label htmlFor="wx-tj-line">Line</label>
                <input
                  id="wx-tj-line"
                  type="text"
                  placeholder="Line ID สำหรับติดต่อ"
                  value={form.lineContact}
                  onChange={(event) => onChange("lineContact", event.target.value)}
                />
              </div>
            </div>
          )}
        </FormSection>

        <FormSection icon={MapPin} title="ที่อยู่บริษัท" sub="ที่อยู่ตามทะเบียน ใช้ออกเอกสารและดูว่านายจ้างอยู่โซนไหน">
          <div className="wx-tj-field">
            <label htmlFor="wx-tj-addressline">ที่อยู่ (เลขที่ ถนน)</label>
            <input
              id="wx-tj-addressline"
              type="text"
              placeholder="เช่น 88/9 ถนนพระราม 3"
              value={form.addressLine}
              onChange={(event) => onChange("addressLine", event.target.value)}
            />
          </div>

          <div className="wx-tj-two">
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-subdistrict">ตำบล / แขวง</label>
              <input
                id="wx-tj-subdistrict"
                type="text"
                value={form.subDistrict}
                onChange={(event) => onChange("subDistrict", event.target.value)}
              />
            </div>
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-district">อำเภอ / เขต</label>
              <input
                id="wx-tj-district"
                type="text"
                value={form.district}
                onChange={(event) => onChange("district", event.target.value)}
              />
            </div>
          </div>

          <div className="wx-tj-two">
            <VocabularySelect
              label="จังหวัด"
              value={form.province}
              onChange={(value) => onChange("province", value)}
              options={employerOptions?.provinces}
              placeholder="เลือกจังหวัด"
            />
            <div className="wx-tj-field">
              <label htmlFor="wx-tj-postal">รหัสไปรษณีย์</label>
              <input
                id="wx-tj-postal"
                type="text"
                inputMode="numeric"
                maxLength={5}
                value={form.postalCode}
                onChange={(event) => onChange("postalCode", event.target.value)}
              />
            </div>
          </div>
        </FormSection>

        {/* P161: logo, cover image and documents. Rendered in both modes - on
            create it explains that files attach once the record exists, which is
            better than an operator finding out after saving. */}
        <EmployerMediaSection employer={isEdit ? employer : null} onChanged={onMediaChanged} />

        {isEdit && (
          <section className="wx-tj-dsec">
            <h3>Agent เจ้าของและสายงาน</h3>
            <div className="wx-tj-kv-row">
              <span>Agent เจ้าของ</span>
              <strong>
                {employer?.agent?.agentCode ?? "-"} · {employer?.agent?.displayName ?? "-"}
              </strong>
            </div>
            <div className="wx-tj-kv-row">
              <span>Super Agent</span>
              <strong>{employer?.line?.superAgentName ?? "-"}</strong>
            </div>
            <div className="wx-tj-kv-row">
              <span>PLC / บมจ</span>
              <strong>{employer?.line?.plcName ?? "-"}</strong>
            </div>
            <p className="wx-tj-field-note">{EDIT_OWNER_NOTE}</p>
          </section>
        )}

        <p className="wx-tj-note-muted wx-tj-note-muted--left">{isEdit ? TAX_ID_DETAIL_NOTE : TAX_ID_FORM_NOTE}</p>

        {submitState.status === "error" && (
          <p className="wx-tj-form-message" role="alert">
            {errorText}
          </p>
        )}

        {isEdit && submitState.status === "success" && (
          <p className="wx-tj-form-message wx-tj-form-message--ok" role="status">
            บันทึกการแก้ไขเรียบร้อยแล้ว
          </p>
        )}

        <div className="wx-tj-drawer-foot">
          {isEdit && <p className="wx-tj-foot-note">{EDIT_UNCHANGED_NOTE}</p>}
          <button type="button" className="wx-button" onClick={onCancel}>
            {isEdit ? "ปิด" : "ยกเลิก"}
          </button>
          <button type="submit" className="wx-button wx-button--primary" disabled={submitting}>
            {submitting ? "กำลังบันทึก..." : isEdit ? "บันทึกการแก้ไข" : "สร้างนายจ้าง"}
          </button>
        </div>
      </form>
    </div>
  );
}
